import styled, { css, keyframes } from 'styled-components'

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const wave = keyframes`
  0% {
    transform: scaleY(0.3);
  }
  50% {
    transform: scaleY(1);
  }
  100% {
    transform: scaleY(0.3);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.micro};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.micro};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

export const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`

export const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 24px 0;
  max-width: 400px;
`

export const RecordButton = styled.button<{ $recording?: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme, $recording }) =>
    $recording ? theme.colors.danger : theme.colors.primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.default};

  ${({ $recording }) =>
    $recording &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const MicrophoneIcon = styled.div<{ $recording?: boolean }>`
  color: white;
  font-size: 32px;
`

export const Timer = styled.div`
  margin-top: 24px;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`

export const VoiceWaveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
  height: 40px;
`

export const VoiceWaveBar = styled.div<{ $delay: number }>`
  width: 4px;
  height: 20px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
  animation: ${wave} 1s ease-in-out ${({ $delay }) => $delay}s infinite;
`

export const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`

export const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid ${({ theme }) => theme.colors.border.light};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

export const ProcessingText = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

export const TranscriptionResult = styled.div`
  width: 100%;
  max-width: 600px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 24px;
  margin-top: 32px;
  text-align: left;
`

export const TranscriptionText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  white-space: pre-wrap;
`

export const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`

export const ErrorMessage = styled.div`
  background: rgba(255, 59, 48, 0.1);
  color: ${({ theme }) => theme.colors.danger};
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: 24px;
  max-width: 400px;
`

export const Instructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  max-width: 400px;
`

export const InstructionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`

// Inline mode components
export const RecordButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex-shrink: 0;
`

export const InlineRecordButton = styled.button<{ $recording?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ theme, $recording }) =>
    $recording ? theme.colors.danger : theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  min-height: 36px;

  ${({ $recording }) =>
    $recording &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}

  &:hover:not(:disabled) {
    background: ${({ theme, $recording }) =>
      $recording ? theme.colors.danger : theme.colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex: 1;
  padding: 1px ${({ theme }) => theme.spacing.default};
  background: rgba(52, 120, 246, 0.08);
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const RecordingStateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
  flex: 1;
`

export const InlineTimer = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  min-width: 48px;
`

export const InlineWaveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 24px;
`

export const InlineWaveBar = styled.div<{ $delay: number }>`
  width: 3px;
  height: 16px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
  animation: ${wave} 1s ease-in-out ${({ $delay }) => $delay}s infinite;
`

export const InlineSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  flex-shrink: 0;
`

export const InlineProcessingText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`
